import { useCallback, useContext } from 'react';
import { MobXProviderContext } from 'mobx-react';
import { GUIDED_LOOKUP_KEY, getGuidedTypeLookupKey } from '@kentik/ui-shared/query/measurements/guidedDimensions';
import type { GuidedValueResolver } from 'app/stores/boards/convertBoardVariablesToFilters';
import { getTagNameFromDimensionKey } from '@kentik/ui-shared/dictionary/v2/compilers/deviceTagMeasurements';

export interface GuidedLookupOption {
  value: string;
  /** Device ID — present when the option comes from the device lookup. */
  id?: string;
  /** Entity name — present when the option comes from the device or site lookup. */
  name?: string;
  label: React.ReactNode;
  matcher?: string;
}

/** A lookup query function compatible with Select's onQuery prop. */
export type GuidedLookupQueryFn = (
  filter: string | undefined,
  options?: Record<string, unknown>
) => Promise<GuidedLookupOption[]>;

/** Result of useGuidedDimensionOptions — either a static list or a dynamic query function. */
export type GuidedDimensionResult = GuidedLookupOption[] | GuidedLookupQueryFn;

/** Minimal shape of the $devices store properties used in this module. */
interface DeviceStore {
  deviceGuidedOptions: GuidedLookupOption[];
  deviceSummariesByName: Record<string, { id: number | string } | undefined>;
}

/** Minimal shape of the $sites store properties used in this module. */
interface SiteStore {
  siteTitleOptions: GuidedLookupOption[];
}

/** Minimal shape of the $lookups store properties used in this module. */
interface LookupStoreShape {
  asNumbers: GuidedLookupQueryFn;
  countries: GuidedLookupQueryFn;
  interfaceDescriptions: GuidedLookupQueryFn;
  snmpAliases: GuidedLookupQueryFn;
  tagValues: GuidedLookupQueryFn;
  getAutoCompleteFilterHandler: (fieldName: string) => GuidedLookupQueryFn | undefined;
}

/** Minimal shape of the $labels store properties used in this module. */
interface LabelStoreShape {
  companyLabels: { models: unknown[] };
  getLabelOptions: (labels: unknown[], includeId?: boolean) => GuidedLookupOption[];
}

export function useGuidedDimensionOptions(guidedType: string | null): GuidedDimensionResult {
  const { $devices, $sites, $lookups, $labels } = useContext(MobXProviderContext) as {
    $devices: DeviceStore;
    $sites: SiteStore;
    $lookups: LookupStoreShape;
    $labels: LabelStoreShape;
  };

  if (!guidedType) {
    return [];
  }

  const lookupKey = getGuidedTypeLookupKey(guidedType);

  switch (lookupKey) {
    case GUIDED_LOOKUP_KEY.Device:
      return $devices.deviceGuidedOptions;
    case GUIDED_LOOKUP_KEY.Site:
      return $sites.siteTitleOptions;
    case GUIDED_LOOKUP_KEY.Label:
      return $labels.getLabelOptions($labels.companyLabels.models);
    case GUIDED_LOOKUP_KEY.ASNumber:
      return $lookups.asNumbers;
    case GUIDED_LOOKUP_KEY.Country:
      return $lookups.countries;
    case GUIDED_LOOKUP_KEY.InterfaceName:
      return $lookups.interfaceDescriptions;
    case GUIDED_LOOKUP_KEY.InterfaceDescription:
      return $lookups.snmpAliases;
    case GUIDED_LOOKUP_KEY.DeviceTag:
      return (filter, options) =>
        $lookups.tagValues(filter, { ...(options || {}), tagName: getTagNameFromDimensionKey(guidedType) });
    case GUIDED_LOOKUP_KEY.SyslogFilterOptions:
    case GUIDED_LOOKUP_KEY.SnmpTrapFilterOptions:
    default: {
      const customHandler = $lookups.getAutoCompleteFilterHandler(guidedType);
      if (customHandler) {
        return customHandler;
      }
      return [];
    }
  }
}

/**
 * Per-field resolvers for guided variable values.
 * Each entry converts a stored name/label into the value the filter field actually expects.
 * Extend this map when a new ID-keyed dimension is added to guidedDimensions.
 */
type FieldResolver = (value: string, stores: { $devices: DeviceStore }) => string;

const guidedFieldResolvers: Partial<Record<string, FieldResolver>> = {
  i_device_id: (value, { $devices }) => {
    const device = $devices.deviceSummariesByName[value];
    return device ? String(device.id) : value;
  },
  device_id: (value, { $devices }) => {
    const device = $devices.deviceSummariesByName[value];
    return device ? String(device.id) : value;
  }
};

/**
 * Returns a stable GuidedValueResolver that converts guided variable values to the
 * correct filter value for each target dimension (e.g. device name → device ID for i_device_id).
 * Intended to be passed directly to convertBoardVariablesToFilters.
 */
export function useGuidedValueResolver(): GuidedValueResolver {
  const { $devices } = useContext(MobXProviderContext) as { $devices: DeviceStore };

  return useCallback(
    (filterField: string, value: string): string => {
      const resolver = guidedFieldResolvers[filterField];
      return resolver ? resolver(value, { $devices }) : value;
    },
    // deviceSummariesByName is a MobX computed; observer handles reactivity,
    // but we re-create the callback when the map reference changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [$devices.deviceSummariesByName]
  );
}
